import {
  getCookies,
  getToken,
  logoutUser,
  // parseKey,
  setCookies,
} from '../utility/helper';
import CryptoJS from 'crypto-js';
// src/lib/handleFormApiError.ts
import { FieldValues, UseFormSetError, Path } from 'react-hook-form';
import { API_ENDPOINT, API_URL } from './constant';

// import { logoutUser } from '@/utility/logoutUser';
// import { store } from '@/redux/store';
// import {
//   logout,
//   setActiveSidebarTab,
//   setAuthenticated,
//   setScrollPosition,
//   setSideBarOpen,
//   setUserData,
//   setUserWebsiteId,
// } from '@/redux/features/UserSlice';
export interface IApiErrorResponse {
  message: string;
  error?: string;
  statusCode: number;
}

interface IRefreshTokenResponse {
  data: string;
}

type AuthExcludedEndpoint =
  | 'login'
  | 'forgot'
  | 'sign-up'
  | 'reset-password'
  | 'regenerate-token';
// | 'logout';

const authExcludedUrls: AuthExcludedEndpoint[] = [
  'login',
  'forgot',
  'sign-up',
  'reset-password',
  'regenerate-token',
  // 'logout',
];

type CallAPIOptions<D = unknown> = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';
  headers?: HeadersInit;
  cache?: RequestCache;
  redirect?: RequestRedirect;
  priority?: RequestPriority;
  signal?: AbortSignal;
  body?: BodyInit;
  multipart?: boolean;
} & (
  | { method: 'GET' | 'HEAD'; data?: Record<string, unknown> }
  | {
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      data?: D;
      multipart?: boolean;
    }
);
export function decryptData(encryptedPayload: string) {
  try {
    if (!encryptedPayload || typeof encryptedPayload !== 'string') {
      console.warn('Invalid encrypted payload:', encryptedPayload);
      return null;
    }
    const textParts = encryptedPayload.split(':');
    const iv = CryptoJS.enc.Hex.parse(textParts.shift()!);
    const encryptedText = CryptoJS.enc.Hex.parse(textParts.join(':'));
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedText,
    });
    // const key = parseKey(import.meta.env.VITE_AES_SECRET_KEY);
    const decrypted = CryptoJS.AES.decrypt(
      cipherParams,
      CryptoJS.enc.Hex.parse(import.meta.env.VITE_AES_SECRET_KEY),
      // key,
      { iv: iv },
    ).toString(CryptoJS.enc.Utf8);
    if (!decrypted || decrypted === '' || decrypted === 'null') {
      console.warn(
        'Decryption returned empty or null text. Possibly wrong key or payload:',
        encryptedPayload,
      );
      return null;
    }
    return decrypted == 'null' ? null : JSON.parse(decrypted);
  } catch (error: unknown) {
    console.error(error);

    return null;
  }
}
export const callApi = async <T, D = unknown>(
  endpoint: string,
  options: CallAPIOptions<D>,
): Promise<T> => {
  const url = new URL(`${endpoint}`);
  const headers = new Headers(options.headers);

  if (!isAuthExcluded(endpoint)) {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  if (['GET', 'HEAD'].includes(options.method) && options.data) {
    Object.entries(options.data).forEach(([key, value]) => {
      if (value !== undefined) url.searchParams.append(key, String(value));
    });
  }

  let body: BodyInit | undefined;
  if (
    ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method) &&
    options.data
  ) {
    if (options.multipart && options.data instanceof FormData) {
      body = options.data;
    } else {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.data);
    }
  }

  const fetchOptions: RequestInit = {
    ...options,
    headers,
    body,
    referrerPolicy: 'strict-origin-when-cross-origin',
  };
  try {
    const response = await fetch(url.toString(), fetchOptions);
    return await handleResponse<T>(response);
  } catch (error) {
    return handleError<T>(error, url.toString(), fetchOptions);
  }
};

// Response handler with proper typing
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) throw response;

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const result = await response.json();
    if (
      result?.data &&
      typeof result.data === 'string' &&
      result.data.includes('https://')
    ) {
      return result;
    } else if (
      result.data &&
      typeof result.data === 'string' &&
      result.data.includes(':')
    ) {
      const decryptedData = decryptData(result.data);
      result.data = decryptedData ?? null;
    }
    return result as T;
  }
  if (contentType?.includes('text/')) {
    return response.text() as Promise<T>;
  }
  if (
    contentType?.includes('octet-stream') ||
    contentType?.includes('image/')
  ) {
    return response.blob() as Promise<T>;
  }
  return response.arrayBuffer() as Promise<T>;
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: Error) => void;
  url: string;
  config: RequestInit;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      if (token) {
        const retryConfig = {
          ...prom.config,
          headers: new Headers(prom.config.headers),
        };
        retryConfig.headers.set('Authorization', `Bearer ${token}`);

        fetch(prom.url, retryConfig)
          .then((response) => handleResponse(response))
          .then((data) => prom.resolve(data))
          .catch((err) => prom.reject(err));
      } else {
        prom.reject(new Error('Failed to refresh token'));
      }
    }
  });
  failedQueue = [];
};

const handleError = async <T>(
  error: unknown,
  url: string,
  config: RequestInit,
): Promise<T> => {
  if (error instanceof TypeError) {
    throw new Error('Network error - failed to connect to server');
  }
  if (error instanceof Response) {
    let shouldRefresh = error.status === 401;
    let errorData: IApiErrorResponse | undefined;

    if (!shouldRefresh && error.status === 500) {
      try {
        errorData = await error.json();
        const msg = errorData?.message;
        if (
          msg === 'jwt malformed' ||
          msg === 'invalid token' ||
          msg === 'invalid signature'
        ) {
          shouldRefresh = true;
        }
      } catch {
        console.log('Error parsing 500 response');
      }
    }

    if (shouldRefresh && !isAuthExcluded(url)) {
      if (isRefreshing) {
        return new Promise<T>((resolve, reject) => {
          failedQueue.push({
            resolve: resolve as (value: unknown) => void,
            reject,
            url,
            config,
          });
        });
      }

      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        processQueue(null, newToken);

        const retryConfig = {
          ...config,
          headers: new Headers(config.headers),
        };
        retryConfig.headers.set('Authorization', `Bearer ${newToken}`);

        const retryResponse = await fetch(url, retryConfig);
        return handleResponse<T>(retryResponse);
      } catch (refreshError) {
        const err =
          refreshError instanceof Error
            ? refreshError
            : new Error('Session expired');
        processQueue(err, null);
        await logoutUser();
        throw new Error('Session expired - please login again');
      } finally {
        isRefreshing = false;
      }
    } else {
      if (!errorData) {
        try {
          errorData = await error.json();
        } catch {
          throw error;
        }
      }
      throw errorData;
    }
  }

  throw new Error('Unknown error occurred');
};

const refreshToken = async (): Promise<string> => {
  try {
    // const deviceToken = getCookies(
    //   `${import.meta.env.VITE_BUSINESS}deviceToken`,
    // );
    const refreshToken = getCookies(
      `${import.meta.env.VITE_BUSINESS}refreshToken`,
    );
    const headers = {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + refreshToken,
    };
    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: headers,
      referrerPolicy: 'strict-origin-when-cross-origin',
    };
    const response = await fetch(
      API_URL + API_ENDPOINT.regenerateToken,
      fetchOptions,
    );
    if (!response.ok) {
      throw new Error('Retrying token failed');
    }
    const responseJson = (await response.json()) as IRefreshTokenResponse;
    let data: { accessToken: string } = { accessToken: '' };
    if (
      responseJson?.data &&
      typeof responseJson?.data === 'string' &&
      responseJson?.data.includes(':')
    ) {
      const decryptedData = decryptData(responseJson?.data);
      data = decryptedData ?? null;
    }
    setCookies(`${import.meta.env.VITE_BUSINESS}token`, data?.accessToken);
    return data?.accessToken;
  } catch (error) {
    console.log(error);
    await logoutUser();

    throw new Error('Failed to refresh session - please login again');
  }
};

// Auth exclusion check
const isAuthExcluded = (endpoint: string): boolean => {
  return authExcludedUrls.some((pattern) => endpoint.includes(pattern));
};

export interface ApiError {
  statusCode: number;
  message?: string;
}

/**
 * Reusable error handler for forms
 * @param err - The API error (with statusCode + message)
 * @param setError - react-hook-form setError function
 * @param map - Mapping of statusCodes to form fields + custom messages
 */
export function handleFormApiError<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
  map: Record<
    number,
    {
      field: Path<T>; // which field gets the error
      message?: string; // custom error message
    }
  >,
) {
  const error = err as Partial<ApiError>;
  const config = map[error?.statusCode ?? -1];

  if (config) {
    setError(config.field, {
      type: 'manual',
      message: config.message || error.message || 'Something went wrong',
    });
  } else {
    setError(Object.keys(map)[0] as Path<T>, {
      type: 'manual',
      message: error?.message || 'Unexpected error occurred',
    });
  }
}
